import {
  Box,
  Icons,
  Loader,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { goToSubmissions, openGroups } from '@app/actions/navigation';
import {
  getAssignmentSubmissions,
  hasFetchedSubmissions,
  hasFetchSubmissionsErrored,
  isFetchingSubmissions,
  selectCurrentAssignmentId,
} from '@core/assignments/selectors';
import { getCurrentSpaceId } from '@core/selectors/space';
import { institutionShortName } from '@core/selectors/institution';
import { AulaState } from '@core/types/state';
import {
  HANDIN_PROVIDER,
  QUIZZES_PROVIDER,
  OVERVIEW_PAGE,
} from '@core/assignments/constants';
import { Assignment } from '@core/assignments/types';
import { createStyles } from './AssignmentDetails.styles';
import NoResultsCard from '@app/components/presentational/common/NoResults/NoResultsCard';
import { emptyAssignmentDetailsText } from '../../../../../../app/constants/texts';
import { ResponsiveAssignment } from '../types';
import AssignmentHeader from '../AssignmentHeader/AssignmentHeader';
import { accessingCurrentSpaceAsStaff } from '@core/selectors/classroom';
import QuizDecommissionBanner from '../SubmissionsResponsive/QuizDecommissionBanner';
import {
  UWS_INSTITUTIONS,
  COVENTRY_INSTITUTIONS,
  NO_STUDENT_MAIN_COHORT,
} from '../constants';
import InfoAlert from '../SubmissionsResponsive/InfoAlert';
import {
  accessToken as getAccessToken,
  currentUser,
} from '@core/selectors/user';
import { operations as assignmentOperations } from '@app/store/assignments';
import {
  formatGroupSubmissions,
  formatUnassignedStudents,
  groupSubmissionsByDate,
} from '../SubmissionsResponsive/GroupSubmissions/formatGroupSubmissions';
import SubmissionAccordions, {
  ResponsiveGroupedSubmissionGroups,
  ResponsiveSubmissionGroups,
  ResponsiveSubmissionItem,
} from '../SubmissionsResponsive/SubmissionAccordions';
import SubmissionSearch, {
  SUBMISSION_SEARCH_STATUS,
  SubmissionSearchResult,
} from '../SubmissionsResponsive/SubmissionSearch';
import AssignmentTypeProvider, {
  useAssignmentTypeProvider,
} from '../SubmissionsResponsive/AssignmentTypeProvider';
import { analyticsTrackEvent } from '@core/utils/analytics';
import * as analyticEvents from '@core/constants/analytics';
import { getAssignmentProvider } from '../utils/launchAssignment';
import GroupFilter, { GroupFilterSet } from '../GroupFilter/GroupFilter';

interface AssignmentDetailsProps {
  assignments: ResponsiveAssignment[];
  currentUserRole: 'educator' | 'staff' | 'student';
  showEmptyCohortAlert?: boolean;
  selectedAssignment?: ResponsiveAssignment | null;
  onMaterialListClick: (assignmentId: string) => void;
}

type MainCohortSubmissionsProps = {
  accessingAsStaff: boolean;
  assignment: Assignment & { groupSet?: string };
  groupedSubmissionGroups?: ResponsiveGroupedSubmissionGroups;
  isEducator: boolean;
  onDeleteSubmission: (submission: ResponsiveSubmissionItem) => Promise<void>;
  filterControl?: React.ReactNode;
  submissionGroups: ResponsiveSubmissionGroups;
  unassignedStudents?: ResponsiveSubmissionItem[];
};

const groupSearchResults = (
  results: SubmissionSearchResult<ResponsiveSubmissionItem>[]
): ResponsiveSubmissionGroups => ({
  earlySubmissions: results
    .filter(({ status }) => status === SUBMISSION_SEARCH_STATUS.onTime)
    .map(({ submission }) => submission),
  lateSubmissions: results
    .filter(({ status }) => status === SUBMISSION_SEARCH_STATUS.late)
    .map(({ submission }) => submission),
  noSubmissions: results
    .filter(({ status }) => status === SUBMISSION_SEARCH_STATUS.noSubmission)
    .map(({ submission }) => submission),
});

const filterGroupedSubmissionGroups = (
  groups: ResponsiveGroupedSubmissionGroups,
  results: SubmissionSearchResult<ResponsiveSubmissionItem>[]
): ResponsiveGroupedSubmissionGroups => {
  const visibleStudentIds = new Set(
    results.map(({ submission }) => String(submission.user.id))
  );
  const filterGroups = (
    groupedSubmissions: ResponsiveGroupedSubmissionGroups['earlySubmissions']
  ) =>
    groupedSubmissions
      .map((group) => ({
        ...group,
        submissions: group.submissions.filter(({ user }) =>
          visibleStudentIds.has(String(user.id))
        ),
      }))
      .filter(({ submissions }) => submissions.length > 0);

  return {
    earlySubmissions: filterGroups(groups.earlySubmissions),
    lateSubmissions: filterGroups(groups.lateSubmissions),
    noSubmissions: filterGroups(groups.noSubmissions),
  };
};

const filterUnassignedStudents = (
  students: ResponsiveSubmissionItem[],
  results: SubmissionSearchResult<ResponsiveSubmissionItem>[]
) => {
  const visibleStudentIds = new Set(
    results.map(({ submission }) => String(submission.user.id))
  );
  return students.filter(({ user }) => visibleStudentIds.has(String(user.id)));
};

const MainCohortSubmissions = ({
  accessingAsStaff,
  assignment,
  groupedSubmissionGroups,
  isEducator,
  onDeleteSubmission,
  filterControl,
  submissionGroups,
  unassignedStudents = [],
}: MainCohortSubmissionsProps) => {
  const { launchFeedback, launchSubmission, type } =
    useAssignmentTypeProvider();

  const openSubmission = (submission: ResponsiveSubmissionItem) => {
    analyticsTrackEvent(analyticEvents.VIEW_STUDENT_SUBMISSION, {
      assignmentId: assignment.id,
    });

    if (type === QUIZZES_PROVIDER) {
      launchFeedback({ submissionId: submission.id });
      return;
    }

    if (type === HANDIN_PROVIDER) {
      launchSubmission({
        submissionId: submission.externalId,
        studentId: String(submission.user.id),
      });
      return;
    }

    launchSubmission({
      submissionExternalId: submission.externalId,
      submissionId: submission.id,
      studentId: String(submission.user.id),
    });
  };

  const renderAccordions = (
    groups: ResponsiveSubmissionGroups,
    groupedGroups = groupedSubmissionGroups,
    expandPopulatedSections = false,
    visibleUnassignedStudents = unassignedStudents
  ) => (
    <SubmissionAccordions
      accessingAsStaff={accessingAsStaff}
      expandPopulatedSections={expandPopulatedSections}
      groupedSubmissionGroups={groupedGroups}
      hideNoSubmissionIdentities={Boolean(assignment.isAnonymised)}
      isEducator={isEducator}
      isGroupAssignment={Boolean(assignment.groupSet)}
      onDeleteSubmission={onDeleteSubmission}
      onOpenSubmission={openSubmission}
      provider={getAssignmentProvider(assignment) || HANDIN_PROVIDER}
      submissionGroups={groups}
      unassignedStudents={visibleUnassignedStudents}
    />
  );

  const searchableGroups = assignment.isAnonymised
    ? { ...submissionGroups, noSubmissions: [] }
    : {
        ...submissionGroups,
        noSubmissions: [
          ...submissionGroups.noSubmissions,
          ...unassignedStudents,
        ],
      };

  return (
    <SubmissionSearch
      filterControl={filterControl}
      isVisible={isEducator && !accessingAsStaff}
      renderResults={(results) =>
        renderAccordions(
          groupSearchResults(results),
          groupedSubmissionGroups
            ? filterGroupedSubmissionGroups(groupedSubmissionGroups, results)
            : undefined,
          true,
          filterUnassignedStudents(unassignedStudents, results)
        )
      }
      submissionGroups={searchableGroups}
    >
      {renderAccordions(submissionGroups)}
    </SubmissionSearch>
  );
};

const AssignmentDetails = ({
  assignments,
  currentUserRole = 'student',
  showEmptyCohortAlert = false,
  selectedAssignment,
  onMaterialListClick,
}: AssignmentDetailsProps) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const dispatch = useDispatch();
  const spaceId = useSelector((state: AulaState) => getCurrentSpaceId(state));
  const routeAssignmentId = useSelector(selectCurrentAssignmentId);
  const institution = useSelector(institutionShortName) as string;
  const assignmentId =
    selectedAssignment?.id || selectedAssignment?.objectId || routeAssignmentId;
  const user = useSelector(currentUser);
  const accessingAsStaff =
    useSelector(accessingCurrentSpaceAsStaff) || currentUserRole === 'staff';
  const accessToken = useSelector((state: AulaState) =>
    getAccessToken(state, spaceId)
  );
  const groupSets = useSelector(
    (state: AulaState) => state.data?.groups?.sets || {}
  );
  const groupFilterSets = useSelector((state: AulaState) => {
    const groupData = state.data?.groups;
    if (!groupData) return [] as GroupFilterSet[];

    return Object.values(groupData.sets || {})
      .filter((set: any) => set?.space === spaceId)
      .flatMap((set: any) => [
        { isHeader: true, key: set.id, label: set.name },
        ...(set.groups || [])
          .map((groupId: string) => groupData.groups?.[groupId])
          .filter(Boolean)
          .map((group: any) => ({
            externalId: set.externalId,
            isHeader: false,
            key: group.id,
            label: group.name,
          })),
      ]);
  });
  const submissions = useSelector((state: AulaState) =>
    assignmentId ? getAssignmentSubmissions(state, assignmentId) : []
  ) as ResponsiveSubmissionItem[];
  const submissionsFetched = useSelector((state: AulaState) =>
    assignmentId ? hasFetchedSubmissions(state, assignmentId) : false
  );
  const submissionsFetching = useSelector((state: AulaState) =>
    assignmentId ? isFetchingSubmissions(state, assignmentId) : false
  );
  const submissionsErrored = useSelector((state: AulaState) =>
    assignmentId ? hasFetchSubmissionsErrored(state, assignmentId) : false
  );
  const [selectedGroupId, setSelectedGroupId] = useState('');

  const isQuizAssignment = selectedAssignment?.provider === QUIZZES_PROVIDER;
  const quizBannerInstitution =
    institution && UWS_INSTITUTIONS.includes(institution)
      ? 'uws'
      : institution && COVENTRY_INSTITUTIONS.includes(institution)
      ? 'coventry'
      : undefined;
  const canViewSubmissionDetails =
    currentUserRole === 'educator' || accessingAsStaff;
  const assignmentWithGroup = selectedAssignment as
    | (ResponsiveAssignment & { groupSet?: string })
    | undefined;
  const isGroupAssignment = Boolean(assignmentWithGroup?.groupSet);
  const submissionGroups = useMemo(
    () =>
      groupSubmissionsByDate(
        submissions,
        selectedAssignment?.endDate || ''
      ) as ResponsiveSubmissionGroups,
    [selectedAssignment?.endDate, submissions]
  );
  const groupedSubmissionGroups = useMemo(
    () =>
      isGroupAssignment
        ? (groupSubmissionsByDate(
            formatGroupSubmissions(submissions),
            selectedAssignment?.endDate || ''
          ) as ResponsiveGroupedSubmissionGroups)
        : undefined,
    [isGroupAssignment, selectedAssignment?.endDate, submissions]
  );
  const unassignedStudents = useMemo(() => {
    if (!assignmentWithGroup?.groupSet) return [];
    const selectedGroupSet = (groupSets as Record<string, any>)[
      assignmentWithGroup.groupSet
    ];
    return formatUnassignedStudents(selectedGroupSet?.studentsWithNoGroup);
  }, [assignmentWithGroup?.groupSet, groupSets]);
  const countGroups = groupedSubmissionGroups || submissionGroups;
  const totalStudentCount =
    countGroups.earlySubmissions.length +
    countGroups.lateSubmissions.length +
    countGroups.noSubmissions.length;
  const totalSubmissionCount =
    countGroups.earlySubmissions.length + countGroups.lateSubmissions.length;
  const submissionCountVsTotal =
    totalStudentCount === 0
      ? '0 students'
      : `${totalSubmissionCount}/${totalStudentCount} submissions`;
  useEffect(() => {
    if (
      !canViewSubmissionDetails ||
      !accessToken ||
      !assignmentId ||
      submissionsFetched ||
      submissionsFetching
    ) {
      return;
    }

    // AssignmentHeader already loads educator Handin submissions for the
    // existing download count. Avoid dispatching that same request twice.
    const headerLoadsHandin =
      currentUserRole === 'educator' &&
      getAssignmentProvider(selectedAssignment || undefined) ===
        HANDIN_PROVIDER;
    if (headerLoadsHandin) return;

    dispatch(
      assignmentOperations.fetchSubmissions({
        accessToken,
        assignmentId,
      })
    );
  }, [
    accessToken,
    assignmentId,
    canViewSubmissionDetails,
    currentUserRole,
    dispatch,
    selectedAssignment,
    submissionsFetched,
    submissionsFetching,
  ]);

  useEffect(() => {
    setSelectedGroupId('');
  }, [assignmentId]);

  // Reuses the legacy submissions route:
  // /dashboard/:spaceId/journey/assignments/:assignmentId/submissions
  const handleViewSubmissions = () => {
    if (!spaceId || !routeAssignmentId) {
      return;
    }
    dispatch(goToSubmissions({ spaceId, assignmentId: routeAssignmentId }));
  };

  const handleDeleteSubmission = async (
    submission: ResponsiveSubmissionItem
  ) => {
    if (!selectedAssignment?.externalId || !assignmentId) {
      throw new Error(
        'The submission cannot be deleted without an assignment ID.'
      );
    }

    const result = await dispatch(
      assignmentOperations.deleteSubmission({
        assignmentExternalId: selectedAssignment.externalId,
        assignmentId,
        groupId:
          isGroupAssignment && submission.group?.id
            ? submission.group.id
            : undefined,
        spaceId,
        studentId:
          isGroupAssignment && submission.group?.id
            ? undefined
            : submission.user.id,
      })
    );

    if ((result as any)?.status === 'error') {
      throw new Error('The submission could not be deleted.');
    }
  };

  const handleGroupFilterChange = (groupId: string) => {
    if (!accessToken || !assignmentId) return;

    setSelectedGroupId(groupId);
    dispatch(
      assignmentOperations.fetchSubmissions({
        accessToken,
        assignmentId,
        group: groupId || null,
      } as any)
    );

    if (groupId) {
      analyticsTrackEvent(analyticEvents.FILTER_SUBMISSIONS_BY_GROUP_ID, {
        activeClassroom: spaceId,
        assignmentId: selectedAssignment?.id,
        groupId,
      });
    }
  };

  const filterControl = (
    <GroupFilter
      groups={groupFilterSets}
      onClear={() => handleGroupFilterChange('')}
      onManageGroups={(queryParams) =>
        dispatch(openGroups(spaceId, queryParams))
      }
      onSelectGroup={handleGroupFilterChange}
      value={selectedGroupId}
    />
  );

  if (assignments?.length === 0 || !selectedAssignment) {
    return (
      <Box sx={styles.emptyWrapper}>
        <NoResultsCard
          title={emptyAssignmentDetailsText[currentUserRole].title}
          description={emptyAssignmentDetailsText[currentUserRole].description}
          sx={styles.noResultsCard}
          titleSx={styles.noResultsCardTitle}
          descriptionSx={styles.noResultsCardDescription}
          icon={<Icons.FolderOffOutlined sx={{ fontSize: 64 }} aria-hidden />}
        />
      </Box>
    );
  }

  return (
    <Box sx={styles.wrapper}>
      {isQuizAssignment && (
        <Box sx={styles.contentBox}>
          <QuizDecommissionBanner institution={quizBannerInstitution} />
        </Box>
      )}
      <AssignmentHeader
        assignment={selectedAssignment}
        page={OVERVIEW_PAGE}
        handleViewSubmissions={handleViewSubmissions}
        isEducator={currentUserRole === 'educator'}
        isStudent={currentUserRole === 'student'}
        accessingAsStaff={accessingAsStaff}
        currentSpaceId={spaceId}
        onMaterialListClick={onMaterialListClick}
      />
      {showEmptyCohortAlert && (
        <Box sx={styles.contentBox}>
          <InfoAlert title={NO_STUDENT_MAIN_COHORT.TITLE} sx={{ mt: '1.5rem' }}>
            {NO_STUDENT_MAIN_COHORT.MESSAGE}
          </InfoAlert>
        </Box>
      )}
      {canViewSubmissionDetails && (
        <Box sx={styles.submissionsSection}>
          <Typography component="h2" sx={styles.submissionsTitle}>
            {`Main cohort (${submissionCountVsTotal})`}
          </Typography>
          {submissionsErrored ? (
            <Typography color="error" role="alert" sx={{ p: 2 }}>
              An error occurred while loading submissions. Please refresh.
            </Typography>
          ) : !submissionsFetched || submissionsFetching ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <Loader size={32} />
            </Box>
          ) : (
            <AssignmentTypeProvider
              assignment={selectedAssignment}
              spaceId={spaceId}
              user={user ?? undefined}
            >
              <MainCohortSubmissions
                accessingAsStaff={accessingAsStaff}
                assignment={
                  assignmentWithGroup as Assignment & {
                    groupSet?: string;
                  }
                }
                groupedSubmissionGroups={groupedSubmissionGroups}
                isEducator={currentUserRole === 'educator'}
                onDeleteSubmission={handleDeleteSubmission}
                filterControl={filterControl}
                submissionGroups={submissionGroups}
                unassignedStudents={unassignedStudents}
              />
            </AssignmentTypeProvider>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AssignmentDetails;
